import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDeferredValue, useId, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView } from '@/card/record'
import { RecordPicker } from '@/card/RecordPicker'
import { formatMoney, formatNumber } from '@/lib/format'
import { Pager } from '@/lists/Pager'
import { AdminState, EditLayer, Notice, SearchBox, SkeletonTable } from '@/admin/AdminShell'
import { AccessDenied } from '@/pages/AccessDenied'
import type { BulkRefusal, PriceImportReport, PriceItem } from './pricingApi'
import {
  PRICE_LISTS_KEY,
  RESOURCE,
  addPriceItems,
  deletePriceItem,
  deletePriceList,
  fetchPriceItems,
  fetchPriceList,
  importPrices,
  updatePriceItem,
} from './pricingApi'
import { PriceListSheet, StateBadge, editDraft, period, type PriceListDraft } from './PriceListsPage'
import '@/styles/admin.css'

const PAGE_SIZE = 50

/**
 * Страница прайс-листа: шапка с реквизитами и таблица цен.
 *
 * Цены листаются страницами по курсору (артикул, id), а не номерами страниц — прайс бывает
 * на десятки тысяч позиций, и OFFSET делал бы дальние страницы дороже первых. Поиск — по
 * артикулу и названию на сервере.
 *
 * Цена правится в строке, товары добавляются выбором нескольких сразу (у каждого своя
 * цена — цены «по умолчанию» нет: ноль в прайсе означал бы бесплатный товар), прайс целиком
 * — импортом CSV «Артикул;Цена». Отказы сервера — его словами: дубль товара, отрицательная
 * цена, строка импорта с неизвестным артикулом называются поимённо.
 */
export function PriceListPage() {
  const { id = '' } = useParams()
  const { t, locale, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const mayRead = can(user, RESOURCE, 'read')
  const mayEdit = can(user, RESOURCE, 'update')
  const mayDelete = can(user, RESOURCE, 'delete')

  const detail = useQuery({
    queryKey: [...PRICE_LISTS_KEY, id],
    enabled: mayRead,
    queryFn: ({ signal }) => fetchPriceList(id, signal),
  })
  const [search, setSearch] = useState('')
  const needle = useDeferredValue(search.trim())
  // Стек курсоров «Назад», привязанный к поиску: при смене поиска он перестаёт действовать
  // в той же отрисовке, иначе ушёл бы запрос со старым курсором.
  const [paging, setPaging] = useState<{ needle: string; cursors: (string | null)[] }>({ needle: '', cursors: [null] })
  const cursors = paging.needle === needle ? paging.cursors : [null]
  const cursor = cursors[cursors.length - 1] ?? null
  const items = useQuery({
    queryKey: [...PRICE_LISTS_KEY, id, 'items', needle, cursor],
    enabled: mayRead,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => fetchPriceItems(id, { search: needle, cursor, limit: PAGE_SIZE }, signal),
  })

  const [listEdit, setListEdit] = useState<PriceListDraft | null>(null)
  const [removingList, setRemovingList] = useState(false)
  const [removingItem, setRemovingItem] = useState<PriceItem | null>(null)
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null)
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: PRICE_LISTS_KEY })
  const fail = (error: unknown) => setNotice(error instanceof Error ? error.message : t('app.error'))

  const savePrice = useMutation({
    mutationFn: (change: { id: string; price: number }) => updatePriceItem(id, change.id, change.price),
    onSuccess: () => { setEditing(null); setNotice(null); refresh() },
    onError: fail,
  })
  const removeItem = useMutation({
    mutationFn: (item: PriceItem) => deletePriceItem(id, item.id),
    onSuccess: () => { setRemovingItem(null); refresh() },
    onError: (error) => { setRemovingItem(null); fail(error) },
  })
  const removeList = useMutation({
    mutationFn: () => deletePriceList(id),
    onSuccess: () => { refresh(); navigate('/price-lists') },
    onError: (error) => { setRemovingList(false); fail(error) },
  })

  // Прямая ссылка минует меню: без права экран говорит «нет доступа», а не крутит загрузку.
  if (!mayRead) return <AccessDenied />

  const list = detail.data
  const page = items.data
  const rows = page?.items ?? []
  const pageIndex = cursors.length - 1
  const from = rows.length === 0 ? 0 : pageIndex * PAGE_SIZE + 1
  const priceError = editing && !validPrice(editing.value) ? t('pl.priceInvalid') : null

  return (
    <>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t('nav.section.sales')}</span>
        <span aria-hidden="true">/</span>
        <Link to="/price-lists">{t('nav.priceLists')}</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{list?.code ?? '…'}</span>
      </nav>
      <AdminState
        isLoading={detail.isLoading}
        error={detail.error}
        onRetry={() => void detail.refetch()}
        forbiddenTitleKey="pl.forbidden"
        errorTitleKey="pl.error"
        skeleton={<SkeletonTable columns={5} rows={6} />}
      >
        {list && (
          <>
            <div className="page-head">
              <h1 className="h1">{list.name}</h1>
              <span className="mono muted t-md">{list.code}</span>
              <span className="badge b-outline mono">{list.currency}</span>
              {list.defaultList && <span className="badge b-info">{t('pl.default')}</span>}
              <StateBadge list={list} />
              <span className="spacer" />
              {mayDelete && (
                <button type="button" className="btn btn-ghost" onClick={() => setRemovingList(true)}
                        disabled={list.counterparties > 0}
                        title={list.counterparties > 0 ? t('pl.deleteBlocked', { count: list.counterparties }) : undefined}>
                  <Icon name="trash" />{t('pl.delete')}
                </button>
              )}
              {mayEdit && (
                <button type="button" className="btn btn-secondary" onClick={() => setListEdit(editDraft(list))}>
                  <Icon name="edit" />{t('admin.edit')}
                </button>
              )}
            </div>
            <p className="page-lead">
              {[period(list, t, locale), t('pl.priorityIs', { priority: list.priority }),
                t('pl.counterpartiesIs', { count: list.counterparties })].join(' · ')}
            </p>
            {!mayEdit && (
              <div className="ro-banner" role="note">
                <Icon name="lock" />
                <span><span className="strong">{t('adm.ro.title')}</span> {t('pl.ro')}</span>
              </div>
            )}
            <Notice text={notice} />

            <section className="panel" aria-labelledby="pl-items-h">
              <div className="panel-h">
                <span className="h2" id="pl-items-h">{t('pl.prices')}</span>
                {page && <span className="count">{formatNumber(page.total, locale)}</span>}
                <span className="spacer" />
                <SearchBox value={search} onChange={setSearch} label={t('pl.items.search')} />
                {mayEdit && (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setImporting(true)}>
                      <Icon name="upload" size="sm" />{t('pl.import')}
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
                      <Icon name="plus" size="sm" />{t('pl.items.add')}
                    </button>
                  </>
                )}
              </div>
              {items.isLoading ? (
                <SkeletonTable columns={5} rows={6} />
              ) : items.error ? (
                <StateView kind="error" compact title={t('pl.error')}
                           description={items.error instanceof Error ? items.error.message : undefined} />
              ) : rows.length === 0 ? (
                needle ? (
                  <StateView kind="empty" compact title={t('pl.items.notFound', { query: needle })} />
                ) : (
                  <StateView kind="empty" compact title={t('pl.items.empty')}
                             description={mayEdit ? t('pl.items.empty.text') : undefined} />
                )
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl" aria-label={t('pl.prices')}>
                    <thead>
                      <tr>
                        <th>{t('pl.sku')}</th>
                        <th>{t('pl.product')}</th>
                        <th>{t('pl.uom')}</th>
                        <th className="num">{t('pl.price', { currency: list.currency })}</th>
                        {mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => {
                        const isEditing = editing?.id === item.id
                        return (
                          <tr key={item.id} className={isEditing ? 'is-selected' : undefined}>
                            <td><span className="mono">{item.sku}</span></td>
                            <td>
                              {item.productName}
                              {!item.productActive && <span className="badge" style={{ marginLeft: 6 }}>{t('pl.productInactive')}</span>}
                              {Number(item.minQuantity) > 0 && (
                                <span className="t-sm muted" style={{ marginLeft: 6 }}>
                                  {t('pl.fromQuantity', { quantity: formatNumber(Number(item.minQuantity), locale) })}
                                </span>
                              )}
                            </td>
                            <td className="muted">{item.uom ?? ''}</td>
                            <td className="num">
                              {isEditing ? (
                                <form className="row" style={{ gap: 6, justifyContent: 'flex-end' }}
                                      aria-label={t('pl.editPrice', { sku: item.sku })}
                                      onSubmit={(event) => {
                                        event.preventDefault()
                                        if (priceError || !editing) return
                                        savePrice.mutate({ id: item.id, price: toNumber(editing.value) })
                                      }}>
                                  <input className={priceError ? 'input input-sm mono is-invalid' : 'input input-sm mono'}
                                         style={{ width: 120, textAlign: 'right' }} inputMode="decimal" autoFocus
                                         aria-label={t('pl.priceFor', { sku: item.sku })} aria-invalid={Boolean(priceError)}
                                         value={editing.value}
                                         onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null) }}
                                         onChange={(e) => setEditing({ id: item.id, value: e.target.value })} />
                                  <button type="submit" className="btn btn-primary btn-sm" disabled={Boolean(priceError) || savePrice.isPending}>
                                    {t('admin.save')}
                                  </button>
                                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>
                                    {t('admin.cancel')}
                                  </button>
                                </form>
                              ) : (
                                <span className="mono">{formatMoney(item.price, list.currency, locale)}</span>
                              )}
                            </td>
                            {mayEdit && (
                              <td className="adm-act">
                                {!isEditing && (
                                  <span className="row" style={{ gap: 4 }}>
                                    <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                            aria-label={t('pl.editPrice', { sku: item.sku })}
                                            onClick={() => setEditing({ id: item.id, value: String(item.price) })}>
                                      <Icon name="edit" size="sm" />
                                    </button>
                                    <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                            aria-label={t('pl.removeItem', { sku: item.sku })}
                                            onClick={() => setRemovingItem(item)}>
                                      <Icon name="trash" size="sm" />
                                    </button>
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <Pager
                from={from}
                to={from === 0 ? 0 : from + rows.length - 1}
                total={page?.total}
                hasPrev={cursors.length > 1}
                hasNext={Boolean(page?.nextCursor)}
                busy={items.isFetching}
                pageSize={PAGE_SIZE}
                onPrev={() => setPaging({ needle, cursors: cursors.slice(0, -1) })}
                onNext={() => page?.nextCursor && setPaging({ needle, cursors: [...cursors, page.nextCursor] })}
              />
            </section>
          </>
        )}
      </AdminState>

      {listEdit && <PriceListSheet draft={listEdit} onChange={setListEdit} onClose={() => setListEdit(null)} />}
      {adding && list && (
        <AddProductsSheet listId={id} currency={list.currency} onClose={() => setAdding(false)} onDone={refresh} />
      )}
      {importing && list && (
        <ImportSheet listId={id} onClose={() => setImporting(false)} onDone={refresh} />
      )}
      <ConfirmModal
        open={removingItem != null}
        title={t('pl.removeItem.confirm', { sku: removingItem?.sku ?? '' })}
        confirmLabel={t('pl.removeItem.do')}
        pending={removeItem.isPending}
        onCancel={() => setRemovingItem(null)}
        onConfirm={() => removingItem && removeItem.mutate(removingItem)}
      >
        <p>{t('pl.removeItem.text')}</p>
      </ConfirmModal>
      <ConfirmModal
        open={removingList}
        title={t('pl.deleteConfirm', { name: list?.name ?? '' })}
        confirmLabel={t('pl.delete')}
        pending={removeList.isPending}
        onCancel={() => setRemovingList(false)}
        onConfirm={() => removeList.mutate()}
      >
        <p>{t('pl.deleteConfirm.text')}</p>
      </ConfirmModal>
    </>
  )
}

/** «1 234,50» и «1234.50» одинаково: так пишет русский Excel и так — английский. */
export function toNumber(value: string): number {
  return Number(value.replace(/[\s ]/g, '').replace(',', '.'))
}

/** Цена: число не меньше нуля и не длиннее четырёх знаков после запятой — как хранит сервер. */
export function validPrice(value: string): boolean {
  const cleaned = value.replace(/[\s ]/g, '').replace(',', '.')
  return /^\d+(\.\d{1,4})?$/.test(cleaned) && Number.isFinite(Number(cleaned))
}

// ------------------------------------------------------------------ добавление товаров

interface Chosen {
  id: string
  label: string
  price: string
}

function AddProductsSheet({ listId, currency, onClose, onDone }: {
  listId: string
  currency: string
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const pickerId = useId()
  const [chosen, setChosen] = useState<Chosen[]>([])
  const [refused, setRefused] = useState<{ label: string; message: string }[]>([])
  const [error, setError] = useState<string | null>(null)
  const invalid = chosen.length === 0 || chosen.some((item) => !validPrice(item.price))

  const save = useMutation({
    mutationFn: () => addPriceItems(listId, chosen.map((item) => ({ productId: item.id, price: toNumber(item.price) }))),
    onSuccess: (result) => {
      onDone()
      if (result.refused.length === 0) {
        onClose()
        return
      }
      // Отказанные остаются в форме с причиной: добавленные ушли, повторять их незачем.
      const labelOf = (refusal: BulkRefusal) => chosen.find((item) => item.id === refusal.productId)?.label ?? refusal.productId ?? ''
      setRefused(result.refused.map((refusal) => ({ label: labelOf(refusal), message: refusal.message })))
      setChosen(chosen.filter((item) => result.refused.some((refusal) => refusal.productId === item.id)))
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('pl.items.add')}
      title={t('pl.items.addTitle')}
      subtitle={t('pl.items.addSubtitle', { currency })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('admin.cancel')}</button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={invalid || save.isPending}>
            {save.isPending ? t('adm.saving') : t('pl.items.addCount', { count: chosen.length })}
          </button>
        </>
      }
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('pl.items.addTitle')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        setRefused([])
        save.mutate()
      }}>
        <div className="field">
          <label className="label" htmlFor={pickerId}>{t('pl.items.pick')}</label>
          <RecordPicker id={pickerId} entity="sales.product" searchColumn="name" extraColumn="sku" value={null}
                        onChange={(picked) => {
                          if (picked && !chosen.some((item) => item.id === picked.id)) {
                            setChosen([...chosen, { id: picked.id, label: picked.label, price: '' }])
                          }
                        }} />
        </div>
        {chosen.length === 0 ? (
          <p className="t-sm muted">{t('pl.items.pickHint')}</p>
        ) : (
          <table className="tbl" aria-label={t('pl.items.chosen')}>
            <thead>
              <tr>
                <th>{t('pl.product')}</th>
                <th className="num">{t('pl.price', { currency })}</th>
                <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {chosen.map((item) => {
                const bad = item.price !== '' && !validPrice(item.price)
                return (
                  <tr key={item.id}>
                    <td>{item.label}</td>
                    <td className="num">
                      <input className={bad ? 'input input-sm mono is-invalid' : 'input input-sm mono'}
                             style={{ width: 120, textAlign: 'right' }} inputMode="decimal"
                             aria-label={t('pl.priceFor', { sku: item.label })} aria-invalid={bad} value={item.price}
                             onChange={(e) => setChosen(chosen.map((other) => other.id === item.id ? { ...other, price: e.target.value } : other))} />
                    </td>
                    <td className="adm-act">
                      <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('pl.items.unpick', { name: item.label })}
                              onClick={() => setChosen(chosen.filter((other) => other.id !== item.id))}>
                        <Icon name="close" size="sm" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {refused.length > 0 && (
          <div className="alert alert-danger" role="alert">
            <Icon name="alert" />
            <span>
              <span className="strong">{t('pl.items.refused')}</span>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {refused.map((item) => <li key={item.label}>{item.label}: {item.message}</li>)}
              </ul>
            </span>
          </div>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

// ------------------------------------------------------------------ импорт

function ImportSheet({ listId, onClose, onDone }: { listId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useApp()
  const fileId = useId()
  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<PriceImportReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: (chosen: File) => importPrices(listId, chosen),
    onSuccess: (result) => { setReport(result); onDone() },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('pl.import')}
      title={t('pl.import.title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{report ? t('adm.close') : t('admin.cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={!file || run.isPending}
                  onClick={() => { if (file) { setError(null); setReport(null); run.mutate(file) } }}>
            {run.isPending ? t('pl.import.running') : t('pl.import.run')}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <p className="t-sm">{t('pl.import.format')}</p>
        <pre className="mono t-sm" style={{ margin: 0 }}>{'Артикул;Цена\nSKU-001;1250,50\nSKU-002;89'}</pre>
        <div className="field">
          <label className="label" htmlFor={fileId}>{t('pl.import.file')}</label>
          <input id={fileId} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>
        {report && (
          <div className={report.failed > 0 ? 'alert alert-warning' : 'alert alert-success'} role="status">
            <Icon name={report.failed > 0 ? 'alert' : 'check'} />
            <span>{t('pl.import.summary', { created: report.created, updated: report.updated, failed: report.failed })}</span>
          </div>
        )}
        {report && report.errors.length > 0 && (
          <table className="tbl" aria-label={t('pl.import.errors')}>
            <thead>
              <tr>
                <th className="num">{t('pl.import.line')}</th>
                <th>{t('pl.import.column')}</th>
                <th>{t('pl.import.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {report.errors.map((row, index) => (
                <tr key={`${row.line}-${index}`}>
                  <td className="num mono">{row.line}</td>
                  <td>{row.column ?? ''}</td>
                  <td>{row.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Notice text={error} />
      </div>
    </EditLayer>
  )
}
